import React from 'react';
import { IInventoryReportType } from '@sp-types/InventoryReportType';
import { FutureOrderExposure } from '../../../sp-types/FutureOrderType';
import { IFinancialPosition } from '../../../sp-types/financialPositionType';

export const SkeletonList: React.FC = () => (
  <div className="flex flex-col flex-1 gap-3 ">
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80 "></div>
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80"></div>
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80"></div>
    <div className=" bg-white flex flex-1 shadow-md basis-4/5 rounded animate-pulse opacity-80"></div>
  </div>
);

export interface BufferMonthData {
  month: string;
  storage: number;
  po: number;
  prevPOs: number;
  prevFutureOrders: number;
  expectedDemand: number;
  buffer: number;
  futureOrder: number;
  visibleExpectedDemand: number;
}

export interface BufferData {
  month: string;
  storage: number;
  po: number;
  expectedDemand: number;
  buffer: number;
  futureOrder: number;
  visibleExpectedDemand: number;
}

export type TimeGranularity = 'Month' | 'Quarter' | 'Terminal';

// Buffer calculation method
export const calculateBufferData = (
  materialId: string,
  inventory: IInventoryReportType[],
  futureOrders: FutureOrderExposure[],
  bufferAmount: number,
  timeGranularity: TimeGranularity = 'Month',
): BufferMonthData[] => {
  const monthsToCalculate = 24;
  const quarterEndMonths = [2, 5, 8, 11]; // March, June, September, December (0-indexed)

  // Get current date info
  const now = new Date();

  // Filter inventory for the selected material
  const materialInventory = inventory.filter((inv) => inv.materialId === materialId && !inv.dontSum);

  // Get ALL inventory (storage) - not just current/previous month
  const currMontInventory = materialInventory.filter(({ isPO }) => !isPO);

  // Get ALL POs including past, current, and future months
  const poInventory = materialInventory.filter((inv) => inv.isPO);

  // Filter future orders for the selected material
  const matFutOrders = futureOrders.filter((order) => order.materialId === materialId);

  let currentStock =
    currMontInventory?.reduce(
      (sum, { quantity, suppliedQty }) => sum + Math.max(0, (quantity || 0) - (suppliedQty || 0)),
      0,
    ) || 0;
  const result: BufferMonthData[] = [];

  // Accumulation variables for buffer and PO only (NOT future orders)
  let accumulatedBuffer = 0;
  let accumulatedPO = 0;
  let accumulatedFutureOrders = 0;

  let prevPOs = 0;
  let prevFutureOrders = 0;

  for (let i = 0; i < monthsToCalculate; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const currYear = monthDate.getFullYear();
    const currMonth = monthDate.getMonth();

    // Find POs for this month
    const monthPOs = poInventory.filter(
      ({ reportingTime }) =>
        reportingTime.toDate().getMonth() === currMonth && reportingTime.toDate().getFullYear() === currYear,
    );
    const monthPOQuantity = Math.round(
      monthPOs.reduce((sum, po) => sum + ((po.quantity || 0) - (po.suppliedQty || 0)), 0),
    );

    // Find future order exposures for this month - DO NOT ACCUMULATE
    const monthExpectedDemand = Math.round(
      matFutOrders
        .filter(({ exposeMonth }) => exposeMonth.month === currMonth + 1 && exposeMonth.year === currYear)
        .reduce((sum, order) => sum + Math.abs(order.exposureValue), 0),
    );

    // Accumulate buffer and PO only
    accumulatedBuffer += bufferAmount;
    accumulatedPO += monthPOQuantity;
    accumulatedFutureOrders += monthExpectedDemand;
    // Determine if we should add this data point based on time granularity
    const shouldAddDataPoint =
      timeGranularity === 'Month' ||
      i === 0 || // Always add current month for quarterly view
      (timeGranularity === 'Quarter' && quarterEndMonths.includes(currMonth));

    if (shouldAddDataPoint) {
      // Format month key as 'MMMYY' (e.g., 'Jan24')
      const monthKey = monthDate
        .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        .replace(' ', '')
        .replace("'", '');

      const expectedDemand = accumulatedFutureOrders + accumulatedBuffer;

      result.push({
        month: monthKey,
        storage: currentStock,
        prevPOs,
        prevFutureOrders,
        buffer: accumulatedBuffer,
        po: accumulatedPO,
        futureOrder: accumulatedFutureOrders,
        expectedDemand,
        visibleExpectedDemand: Math.max(0, expectedDemand - accumulatedPO - currentStock),
        visExpectedMinusStock: Math.max(0, expectedDemand - currentStock),
        visExpectedMinusPO: Math.max(0, expectedDemand - accumulatedPO),
      } as BufferMonthData);

      currentStock = Math.max(0, currentStock - accumulatedFutureOrders + accumulatedPO);
      prevPOs = accumulatedPO;
      prevFutureOrders = accumulatedFutureOrders;

      accumulatedBuffer = 0;
      accumulatedPO = 0;
      accumulatedFutureOrders = 0;
    }
  }

  return result;
};

export interface LogisticsMonthData {
  month: string;
  stock: number;
  po: number;
  futureOrders: number;
  netInventory: number; // stock + po - futureOrders
  shortage: number; // if netInventory < 0
  mrpHidden: number | null; // if netInventory >= 0, shows futureOrders
  requiredStock: number; // red line threshold
}

// Required stock percentages for 12 months (repeating pattern for 24 months)
export const DEFAULT_STRATEGY = [100, 100, 100, 75, 75, 75, 50, 50, 50, 25, 25, 25, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

export interface IOrdersInventoryBal {
  month: string;
  POs: number;
  inventoryRep: number;
  futureOrders: number;
  visFutOrders: number;
  visCovExpectDemand: number;
  financialPositions: number;
  stock: number;
  stockSurplus: number;
  inventory: number;
  remain: number;
  emergencyStock: number;
  covRatio: number;
  logisticAmnt: number;
  expectedDemand: number;
  visExpectDemand: number;
  requiredQuantity: number;
  reqCover: number;
  stockNoRatio: number;
  sumPoNoRatio: number;
  currCover: number;
}
export const calculateStockDeltas = (
  materialId: string,
  inventory: IInventoryReportType[],
  futureOrders: FutureOrderExposure[],
  financialPos: IFinancialPosition[],
  isCoverage: boolean,
  coverageRatio: number[],
  isCovPercent: boolean,
  logisticsAmount: number[],
  isLogisticsPercent: boolean,
  emergencyStock: number,
  timeGranularity: TimeGranularity = 'Month',
): IOrdersInventoryBal[] => {
  const monthsToCalculate = 24;

  // Get current date info
  const now = new Date();

  // Filter inventory for the selected material
  const materialInventory = inventory.filter((inv) => inv.materialId === materialId && !inv.dontSum);

  // Get ALL inventory (storage) - not just current/previous month
  const currMontInventory = materialInventory.filter(({ isPO }) => !isPO);

  // Get ALL POs including past, current, and future months
  const poInventory = materialInventory.filter((inv) => inv.isPO);

  // Filter future orders for the selected material
  const matFutOrders = futureOrders.filter((order) => order.materialId === materialId);

  let currentStock =
    currMontInventory?.reduce(
      (sum, { quantity, suppliedQty }) => sum + Math.max(0, (quantity || 0) - (suppliedQty || 0)),
      0,
    ) || 0;
  let stockNoRatio = currentStock;
  const result: IOrdersInventoryBal[] = [];

  for (let i = 0; i < monthsToCalculate; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const currYear = monthDate.getFullYear();
    const currMonth = monthDate.getMonth();

    // Find POs for this month
    const monthPOsNoRatio = poInventory.filter(
      ({ reportingTime, ratio }) =>
        reportingTime.toDate().getMonth() === currMonth && reportingTime.toDate().getFullYear() === currYear && !ratio,
    );
    const sumPoNoRatio = Math.round(
      monthPOsNoRatio.reduce((sum, po) => sum + ((po.quantity || 0) - (po.suppliedQty || 0)), 0),
    );

    const monthPOsRatio = poInventory.filter(
      ({ reportingTime, ratio }) =>
        reportingTime.toDate().getMonth() === currMonth && reportingTime.toDate().getFullYear() === currYear && ratio,
    );
    const sumPoRatio = Math.round(
      monthPOsRatio.reduce((sum, po) => sum + ((po.quantity || 0) - (po.suppliedQty || 0)), 0),
    );

    // Find future order exposures for this month
    const monthFutureOrders = Math.round(
      matFutOrders
        .filter(({ exposeMonth }) => exposeMonth.month === currMonth + 1 && exposeMonth.year === currYear)
        .reduce((sum, order) => sum + order.exposureValue, 0),
    );

    // Find financial positions for this month if coverage is enabled
    const monthFinancialPositions = financialPos
      .filter(
        (pos) =>
          pos.materialId === materialId &&
          pos.expiryDate.toDate().getMonth() === currMonth &&
          pos.expiryDate.toDate().getFullYear() === currYear,
      )
      .reduce((sum, pos) => sum + (pos.routeQuantity || 0), 0);

    const monthKey = monthDate
      .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      .replace(' ', '')
      .replace("'", '');

    const expectedDemand = monthFutureOrders + emergencyStock;
    const currCover = Math.max(0, Math.round(stockNoRatio + sumPoNoRatio + monthFinancialPositions));

    // Always treat strategy values as percentages (divide by 100)
    const coveragePercent = coverageRatio[i] === 0 ? 0 : coverageRatio[i] || DEFAULT_STRATEGY[i];
    const logisticsPercent = logisticsAmount[i] === 0 ? 0 : logisticsAmount[i] || DEFAULT_STRATEGY[i];

    const reqCover = expectedDemand * (coveragePercent / 100);
    const requiredQuantity = monthFutureOrders * (logisticsPercent / 100);

    // Calculate net inventory: stock + PO - future orders
    const remain = currentStock + sumPoNoRatio + sumPoRatio - monthFutureOrders;
    const inventory = Math.max(0, currentStock - emergencyStock + sumPoNoRatio + sumPoRatio);

    const monthRow = {
      month: monthKey,
      POs: sumPoNoRatio + sumPoRatio,
      inventoryRep: sumPoNoRatio + currentStock,
      futureOrders: monthFutureOrders,
      visFutOrders: Math.max(0, monthFutureOrders - inventory), //מיותר
      stock: currentStock,
      stockSurplus: Math.max(0, currentStock - emergencyStock),
      inventory,
      remain,
      expectedDemand,
      visExpectDemand: Math.max(0, expectedDemand - inventory),
      visCovExpectDemand: Math.max(0, expectedDemand - currCover),
      requiredQuantity,
      reqCover,
      covPercentage: reqCover === 0 ? 0 : Math.round((currCover / (reqCover || 1)) * 100),
      currCover,
      financialPositions: monthFinancialPositions,
      stockNoRatio,
      sumPoNoRatio,
      emergencyStock,
      covRatio: coveragePercent,
      logisticAmnt: logisticsPercent,
    } as IOrdersInventoryBal;

    result.push(monthRow);

    currentStock = Math.max(0, remain);
    stockNoRatio = Math.max(0, stockNoRatio + sumPoNoRatio - monthFutureOrders);
  }

  return result;
};

export const shorten = (num: number) =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
